import type { List, StundenplanPausenzeit, StundenplanZeitraster, Wochentag } from "@core";
import { ArrayList, DeveloperNotificationException, Stundenplan, StundenplanManager } from "@core";

import { api } from "~/router/Api";
import { RouteData, type RouteStateInterface } from "~/router/RouteData";
import { routeStundenplan } from "../RouteStundenplan";

interface RouteStateKatalogZeitraster extends RouteStateInterface {
	listKatalogeintraege: List<StundenplanZeitraster>;
	stundenplanManager: StundenplanManager | undefined;
	selected: Wochentag | number | StundenplanZeitraster | StundenplanPausenzeit | undefined;
}

const defaultState = <RouteStateKatalogZeitraster> {
	listKatalogeintraege: new ArrayList(),
	stundenplanManager: undefined,
	selected: undefined,
};

export class RouteDataKatalogZeitraster extends RouteData<RouteStateKatalogZeitraster> {

	public constructor() {
		super(defaultState);
	}

	get stundenplanManager(): StundenplanManager {
		if (this._state.value.stundenplanManager === undefined) {
			throw new DeveloperNotificationException("Unerwarteter Fehler: Stundenplandaten nicht initialisiert");
		}
		return this._state.value.stundenplanManager;
	}

	get selected(): Wochentag | number | StundenplanZeitraster | StundenplanPausenzeit | undefined {
		return this._state.value.selected;
	}

	public setSelection = (value: Wochentag | number | StundenplanZeitraster | StundenplanPausenzeit | undefined) => {
		this.setPatchedState({ selected: value });
	};

	public async ladeListe() {
		api.status.start();
		const listZeitraster = await api.server.getZeitraster(api.schema);
		const stundenplan = new Stundenplan();
		stundenplan.zeitraster = listZeitraster;
		stundenplan.gueltigAb = "1979-07-10";
		stundenplan.gueltigBis = "2050-09-29";
		const stundenplanManager = new StundenplanManager(stundenplan, new ArrayList(), new ArrayList(), null);
		const defaults = routeStundenplan.data.settingsDefaults;
		stundenplanManager.stundenplanKonfigSet(defaults);
		this.setPatchedDefaultState({ stundenplanManager });
		api.status.stop();
	}

	addZeitraster = async (zeitraster: Iterable<Partial<StundenplanZeitraster>>) => {
		api.status.start();
		const list = new ArrayList<Partial<StundenplanZeitraster>>();
		for (const z of zeitraster) {
			delete z.id;
			list.add(z);
		}
		const items = await api.server.addZeitrasterEintraege(list, api.schema);
		this.stundenplanManager.zeitrasterAddAll(items);
		await routeStundenplan.data.reloadVorlagen();
		this.commit();
		api.status.stop();
	};

	removeZeitraster = async (zeitraster: Iterable<StundenplanZeitraster>) => {
		api.status.start();
		const list = new ArrayList<number>();
		for (const z of zeitraster) {
			list.add(z.id);
		}
		const items = await api.server.deleteZeitrasterEintraege(list, api.schema);
		this.stundenplanManager.zeitrasterRemoveAll(items);
		this._state.value.selected = undefined;
		await routeStundenplan.data.reloadVorlagen();
		this.commit();
		api.status.stop();
	};

	patchZeitraster = async (zeitraster: Iterable<StundenplanZeitraster>) => {
		api.status.start();
		const list: List<StundenplanZeitraster> = new ArrayList();
		for (const z of zeitraster) {
			list.add(z);
		}
		await api.server.patchZeitrasterEintraege(list, api.schema);
		this.stundenplanManager.zeitrasterPatchAttributesAll(list);
		await routeStundenplan.data.reloadVorlagen();
		this.commit();
		api.status.stop();
	};
}

